import { Spinner } from "@blueprintjs/core";
import { FGDateMaskInput, FGListen, FGSelectInput, FieldSet, useApiEffect } from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { CharacteristicApi, DogDysplasiaApi, DogDysplasiaDto, EHealthValueType } from "../../../../../api";
import { FGSRSHDateMaskInput, SmallFormGenerator } from "../../../../../components";
import { FGNumberMaskInput } from "../../../../../components/formGenerator/FGNumberMaskInput";
import { useGlobalData } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IDogDysplasiaPanelProps {
  dogId: number;
  dogDysplasiaId: number;
  postSave: () => void;
}

export const DogDysplasiaPanel: React.FunctionComponent<IDogDysplasiaPanelProps> = ({
  dogId,
  dogDysplasiaId,
  postSave,
}) => {
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const api = useApiService(DogDysplasiaApi);
  const characteristicApi = useApiService(CharacteristicApi);
  const [characteristicId, setcharacteristicId] = React.useState<number>();
  const { openNewTab } = useWindow();

  const saveCallback = React.useCallback(
    async (toSave: DogDysplasiaDto) => {
      await api.save(toSave);
      postSave();
    },
    [api, postSave]
  );
  const { data, loading, saving, saveItem } = useCrudApi<DogDysplasiaDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = dogDysplasiaId) => (id > 0 ? api.get(id) : new DogDysplasiaDto({ dogId: dogId }).toJSON()),
        saveApiFn: (d: DogDysplasiaDto) => saveCallback(d),
      }),
      [api, dogDysplasiaId, dogId, saveCallback]
    ),
    [dogId]
  );

  const [healthValueType, htvLoading] = useApiEffect(() => {
    if (
      !!characteristicId &&
      (!data?.healthValueType || (!loading && data && data.characteristicId !== characteristicId && !!characteristicId))
    ) {
      return characteristicApi.getHealthValueTypeCode(characteristicId);
    } else {
      return data?.healthValueType;
    }
  }, [characteristicApi, characteristicId, data, loading]);

  const [characteristic, cloading] = useReferential(
    (a) => a.getCharacteristics(true, currentLanguage.languageId),
    true,
    []
  );

  const [healthValues, hvloading] = useReferential(
    (a) => characteristicId && a.getHealthValues(characteristicId),
    true,
    [characteristicId]
  );

  const ComputeResultType = React.useCallback(() => {
    if (htvLoading) {
      return <Spinner />;
    } else {
      switch (healthValueType) {
        case EHealthValueType.Numeric:
          return <FGNumberMaskInput name="numericValue" label={t(ETLCodes.Value)} />;
        case EHealthValueType.ValueList:
          return (
            <FGSelectInput name="healthValueId" label={t(ETLCodes.Value)} items={healthValues} loading={hvloading} />
          );
        default:
          return null;
      }
    }
  }, [healthValueType, healthValues, htvLoading, hvloading, t]);
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      characteristicId: Yup.number().required(t(ETLCodes.Required)),
    });
  }, [t]);
  return (
    <SmallFormGenerator
      validationSchema={FormSchema}
      onSubmit={saveItem}
      initialValues={data}
      editMode={true}
      saving={saving}
      loading={loading}
    >
      <FGListen field="characteristicId" onChanged={(d: number) => setcharacteristicId(d)} />
      <FieldSet>
        <FGSRSHDateMaskInput name="radioDate" label={t(ETLCodes.RadioDate)} />
        <FGSRSHDateMaskInput name="evaluationDate" label={t(ETLCodes.EvaluationDate)} />
        <FGSelectInput
          name="characteristicId"
          label={t(ETLCodes.Characteristic)}
          loading={cloading}
          items={characteristic}
          onItemViewClick={(item) => openNewTab(`${ERoutes.characteristics}/${item}/detail/view`)}
        />

        <ComputeResultType />
      </FieldSet>
    </SmallFormGenerator>
  );
};
